import { useState, useEffect } from "react";
import { api } from "../api";
import toast from "./Toast";

// Gumroad product IDs
const GUMROAD_PRODUCTS = {
  hustler:      "jezzf",
  professional: "kzfecu",
  business:     "aagip",
  enterprise:   "qwxysz",
};

const GUMROAD_BASE = "https://gumroad.com/l";

export default function GumroadCheckout({ plan, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [currentSub, setCurrentSub] = useState(null);

  useEffect(() => {
    api.get("/subscription")
      .then(({ data }) => setCurrentSub(data))
      .catch(() => {});
  }, []);

  // Listen for Gumroad success via postMessage
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin.includes("gumroad.com")) {
        if (event.data?.type === "purchase" || event.data?.sale) {
          setLoading(false);
          try {
            const { data } = await api.get("/subscription");
            setCurrentSub(data);
            if (onSuccess) onSuccess(data);
          } catch (_) {}
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onSuccess]);

  const isCurrentPlan = currentSub?.tier === plan.tier;
  const isFree = plan.tier === "free";

  const handleClick = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.warn("Please log in to purchase a plan.");
      return;
    }

    const productId = GUMROAD_PRODUCTS[plan.tier];
    if (!productId) return;

    // Get user ID from JWT
    let userId = "unknown";
    let userEmail = "";
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      userId = payload.sub || payload.identity || "unknown";
      userEmail = payload.email || "";
    } catch (_) {}

    setLoading(true);

    // Build Gumroad URL with user tracking
    const url = `${GUMROAD_BASE}/${productId}?wanted=true&email=${encodeURIComponent(userEmail)}&user_id=${userId}`;
    window.open(url, "_blank");

    // Reset loading after timeout
    setTimeout(() => setLoading(false), 30000);
  };

  // Free plan
  if (isFree) {
    return (
      <button
        className="w-full py-3 rounded-xl font-semibold text-sm transition-all bg-white/10 hover:bg-white/15 text-gray-300 border border-white/10"
        onClick={() => onSuccess && onSuccess({ tier: "free" })}
      >
        {plan.cta || "Get Started Free"}
      </button>
    );
  }

  // Current plan
  if (isCurrentPlan) {
    return (
      <button
        className="w-full py-3 rounded-xl font-semibold text-sm bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-default"
        disabled
      >
        ✓ Current Plan
      </button>
    );
  }

  const hsl = `${plan.hue},82%,51%`;

  return (
    <button
      className="w-full py-3 rounded-xl font-semibold text-sm transition-all text-white shadow-lg hover:scale-[1.02] disabled:opacity-50 disabled:cursor-wait"
      style={{
        background: loading
          ? "rgba(255,255,255,0.08)"
          : `linear-gradient(135deg, hsl(${hsl}), hsl(${plan.hue + 40},82%,45%))`,
        boxShadow: loading ? "none" : `0 4px 20px hsla(${hsl},0.3)`,
      }}
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2 text-gray-400">
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20" />
          </svg>
          Opening Checkout...
        </span>
      ) : (
        plan.cta || "Subscribe Now"
      )}
    </button>
  );
}
